import { useState, useEffect, useRef, useCallback } from 'react'
import { Link } from 'react-router-dom'
import { Search, Filter, Download, FileText, AlertCircle, LayoutGrid, BarChart3, RefreshCw, FileSpreadsheet, Inbox } from 'lucide-react'
import { PublicationService } from '@/services/publicationService'
import { djenSyncService } from '@/services/djen-sync.service'
import { DJENPublication, PublicationFilters, PublicationMetrics, WeeklyMetrics, DayOfWeek, PublicationStatus, PublicationPriority } from '@/types/publication'
import { Card } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from '@/components/ui/dropdown-menu'
import { Input } from '@/components/ui/input'
import { Pagination } from '@/components/ui/Pagination'
import { PublicationTable } from '@/components/publications/PublicationTable'
import { PublicationFullSheet } from '@/components/publications/inbox/PublicationFullSheet'
import { PublicationQuickFilters, QuickFilterType } from '@/components/publications/PublicationQuickFilters'
import { MetricsCards } from '@/components/publications/MetricsCards'
import { CompactMetricsBar } from '@/components/publications/CompactMetricsBar'
import { WeekNavigator } from '@/components/publications/WeekNavigator'
import { WeekComparisonBadges, WeeklyComparison } from '@/components/publications/WeekComparisonBadges'
import { WeeklyTrendChart, WeeklyTrendData } from '@/components/publications/WeeklyTrendChart'
import { CalendarHeatmap, MonthlyHeatmapData } from '@/components/publications/CalendarHeatmap'
import { StatusDistributionCharts, StatusDistributionData } from '@/components/publications/StatusDistributionCharts'
import { TopTribunalsChart, TopTribunalData } from '@/components/publications/TopTribunalsChart'
import { ProcessingFunnelChart, ProcessingFunnelData } from '@/components/publications/ProcessingFunnelChart'
import { LoadingSpinner } from '@/components/LoadingSpinner'
import { startOfWeek, format } from 'date-fns'
import { toast } from 'sonner'

import { getErrorMessage } from '../utils/error.utils'
export function PublicationsPage() {
  const [publications, setPublications] = useState<DJENPublication[]>([])
  const [metrics, setMetrics] = useState<PublicationMetrics | null>(null)
  const [weeklyMetrics, setWeeklyMetrics] = useState<WeeklyMetrics | null>(null)
  const [weeklyComparison, setWeeklyComparison] = useState<WeeklyComparison | null>(null)
  const [weeklyTrend, setWeeklyTrend] = useState<WeeklyTrendData | null>(null)
  const [monthlyHeatmap, setMonthlyHeatmap] = useState<MonthlyHeatmapData | null>(null)
  const [statusDistribution, setStatusDistribution] = useState<StatusDistributionData | null>(null)
  const [topTribunals, setTopTribunals] = useState<TopTribunalData[] | null>(null)
  const [processingFunnel, setProcessingFunnel] = useState<ProcessingFunnelData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [selectedPublication, setSelectedPublication] = useState<DJENPublication | null>(null)
  const [showFilters, setShowFilters] = useState(false)
  const [syncingPublications, setSyncingPublications] = useState(false)
  const [exportingPublications, setExportingPublications] = useState(false)

  // Estado para navegação de mês no heatmap (Fase 2.1)
  const [currentHeatmapMonth, setCurrentHeatmapMonth] = useState(() => {
    const today = new Date()
    return { year: today.getFullYear(), month: today.getMonth() + 1 }
  })
  const [selectedHeatmapDate, setSelectedHeatmapDate] = useState<string | null>(null)

  // Toggle entre visualizações
  const [viewMode, setViewMode] = useState<'classic' | 'weekly'>('weekly')
  const [selectedDay, setSelectedDay] = useState<DayOfWeek>(() => {
    const days: DayOfWeek[] = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday']
    return days[new Date().getDay()]
  })

  // Toggle para mostrar/ocultar gráficos grandes
  const [showDetailedCharts, setShowDetailedCharts] = useState(false)

  // Estado de navegação de semana (Fase 1.1)
  const [currentWeekStart, setCurrentWeekStart] = useState<Date>(() =>
    startOfWeek(new Date(), { weekStartsOn: 1 }) // Monday = 1
  )

  // Separar o termo de busca do estado de filtros para debounce
  const [searchTerm, setSearchTerm] = useState('')
  const searchDebounceRef = useRef<NodeJS.Timeout>()

  const [filters, setFilters] = useState<PublicationFilters>({
    page: 1,
    limit: 20,
    search: '',
    analysisStatus: undefined,
    priority: undefined,
    tribunal: '',
    tipoComunicacao: ''
  })

  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0
  })

  // Estados para filtros rápidos (Fase 2)
  const [activeQuickFilter, setActiveQuickFilter] = useState<QuickFilterType>('ALL')
  const [quickFilterCounts, setQuickFilterCounts] = useState({
    needsAction: 0,
    inProcess: 0,
    done: 0,
    total: 0
  })

  // Debounce para busca - aguarda 500ms após o usuário parar de digitar
  useEffect(() => {
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current)
    }

    searchDebounceRef.current = setTimeout(() => {
      // Skip when the term did not change (e.g. on mount): returning the same
      // object avoids a redundant reload that flashes the loading spinner.
      setFilters(prev => prev.search === searchTerm ? prev : ({
        ...prev,
        search: searchTerm,
        page: 1
      }))
    }, 500)

    return () => {
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current)
      }
    }
  }, [searchTerm])

  const loadPublications = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      const publicationsResponse = await PublicationService.getPublications(filters)
      if (publicationsResponse.success) {
        setPublications(publicationsResponse.data)
        if (publicationsResponse.pagination) {
          setPagination(publicationsResponse.pagination)
        }
      }
    } catch (err) {
      console.error('Erro ao carregar publicações:', err)
      setError('Erro ao carregar publicações. Tente novamente.')
    } finally {
      setLoading(false)
    }
  }, [filters])

  const loadMetrics = useCallback(async () => {
    try {
      const weekStartFormatted = format(currentWeekStart, 'yyyy-MM-dd')

      const [metricsResponse, weeklyMetricsResponse, weeklyComparisonResponse, weeklyTrendResponse, heatmapResponse, distributionResponse, topTribunalsResponse, funnelResponse, quickFilterCountsResponse] = await Promise.all([
        PublicationService.getMetrics(),
        PublicationService.getWeeklyMetrics(weekStartFormatted),
        PublicationService.getWeeklyComparison<WeeklyComparison>(weekStartFormatted),
        PublicationService.getWeeklyTrend<WeeklyTrendData>(4),
        PublicationService.getMonthlyHeatmap<MonthlyHeatmapData>(currentHeatmapMonth.year, currentHeatmapMonth.month),
        PublicationService.getStatusDistribution<StatusDistributionData>(),
        PublicationService.getTopTribunals<TopTribunalData[]>(5, weekStartFormatted),
        PublicationService.getProcessingFunnel<ProcessingFunnelData>(weekStartFormatted),
        PublicationService.getQuickFilterCounts()
      ])

      if (metricsResponse.success) setMetrics(metricsResponse.data)
      if (weeklyMetricsResponse.success) setWeeklyMetrics(weeklyMetricsResponse.data)
      if (weeklyComparisonResponse.success) setWeeklyComparison(weeklyComparisonResponse.data)
      if (weeklyTrendResponse.success) setWeeklyTrend(weeklyTrendResponse.data)
      if (heatmapResponse.success) setMonthlyHeatmap(heatmapResponse.data)
      if (distributionResponse.success) setStatusDistribution(distributionResponse.data)
      if (topTribunalsResponse.success) setTopTribunals(topTribunalsResponse.data)
      if (funnelResponse.success) setProcessingFunnel(funnelResponse.data)
      if (quickFilterCountsResponse.success) setQuickFilterCounts(quickFilterCountsResponse.data)
    } catch (err) {
      console.error('Erro ao carregar métricas:', err)
    }
  }, [currentWeekStart, currentHeatmapMonth.year, currentHeatmapMonth.month])

  // Recarregar publicações filtradas quando filtros ou paginação mudam
  useEffect(() => {
    loadPublications()
  }, [loadPublications])

  // Recarregar métricas quando semana ou mês do heatmap mudam (não dependem dos filtros de lista)
  useEffect(() => {
    loadMetrics()
  }, [loadMetrics])

  const loadData = async () => {
    await Promise.all([loadPublications(), loadMetrics()])
  }

  const handleSearch = (value: string) => {
    setSearchTerm(value)
  }

  const handlePageChange = (page: number) => {
    setFilters(prev => ({ ...prev, page }))
  }

  const handleViewPublication = (publication: DJENPublication) => {
    setSelectedPublication(publication)
  }

  const handleCloseDetails = useCallback(() => {
    setSelectedPublication(null)
  }, [])

  const handlePublicationUpdate = () => {
    loadData() // Recarregar dados após atualização
  }

  const handleDayChange = (day: DayOfWeek) => {
    setSelectedDay(day)

    // Filtrar publicações para o dia selecionado
    if (weeklyMetrics && weeklyMetrics.byDayOfWeek[day]) {
      const dayData = weeklyMetrics.byDayOfWeek[day]
      setFilters(prev => ({
        ...prev,
        dataInicio: dayData.date,
        dataFim: dayData.date,
        page: 1
      }))
    }
  }

  const handleToggleView = () => {
    setViewMode(prev => prev === 'classic' ? 'weekly' : 'classic')

    // Se mudar para visualização clássica, limpar filtros de data
    if (viewMode === 'weekly') {
      setFilters(prev => ({
        ...prev,
        dataInicio: '',
        dataFim: ''
      }))
    }
  }

  // Handler de mudança de semana (Fase 1.1)
  const handleWeekChange = (newWeekStart: Date) => {
    setCurrentWeekStart(newWeekStart)
  }

  // Handlers do Calendar Heatmap (Fase 2.1)
  const handleHeatmapMonthChange = (year: number, month: number) => {
    setCurrentHeatmapMonth({ year, month })
  }

  const handleHeatmapDayClick = (day: { date: string }) => {
    setSelectedHeatmapDate(day.date)

    setFilters(prev => ({
      ...prev,
      dataInicio: day.date,
      dataFim: day.date,
      page: 1
    }))
  }

  // Handler de filtros rápidos (Fase 2)
  const handleQuickFilterChange = (filterType: QuickFilterType) => {
    setActiveQuickFilter(filterType)

    // Limpar filtros manuais ao aplicar filtro rápido
    setSearchTerm('')

    switch (filterType) {
      case 'NEEDS_ACTION':
        // Intimações sem tarefa criada e não arquivadas
        setFilters({
          page: 1,
          limit: 20,
          search: '',
          tipoComunicacao: 'Intimação',
          analysisStatus: undefined,
          priority: undefined,
          tribunal: '',
          dataInicio: '',
          dataFim: ''
        })
        break

      case 'IN_PROCESS':
        // Tarefas criadas ou vinculadas a processo (não arquivadas) — backend filtra por grupo
        setFilters({
          page: 1,
          limit: 20,
          search: '',
          analysisStatus: 'AUTO_LINKED' as PublicationStatus,
          tipoComunicacao: '',
          priority: undefined,
          tribunal: '',
          dataInicio: '',
          dataFim: ''
        })
        break

      case 'DONE':
        // Arquivadas
        setFilters({
          page: 1,
          limit: 20,
          search: '',
          analysisStatus: 'ARCHIVED' as never,
          tipoComunicacao: '',
          priority: undefined,
          tribunal: '',
          dataInicio: '',
          dataFim: ''
        })
        break

      case 'ALL':
      default:
        // Limpar todos os filtros
        setFilters({
          page: 1,
          limit: 20,
          search: '',
          analysisStatus: undefined,
          tipoComunicacao: '',
          priority: undefined,
          tribunal: '',
          dataInicio: '',
          dataFim: ''
        })
        break
    }
  }

  // Handler para importação manual de publicações
  const handleManualSync = async () => {
    try {
      setSyncingPublications(true)
      toast.loading('Importando publicações do DJEN... Isso pode levar alguns minutos.', {
        id: 'sync-publications'
      })

      const result = await djenSyncService.triggerManualSync()

      if (result.success) {
        toast.success(result.message || 'Publicações importadas com sucesso!', {
          id: 'sync-publications',
          duration: 5000
        })

        // Recarregar os dados após a importação
        await loadData()
      } else {
        toast.error(result.message || 'Erro ao importar publicações', {
          id: 'sync-publications'
        })
      }
    } catch (error: unknown) {
      console.error('Error syncing publications:', error)
      toast.error(getErrorMessage(error, 'Erro ao importar publicações'), {
        id: 'sync-publications'
      })
    } finally {
      setSyncingPublications(false)
    }
  }

  // Colunas exportadas — compartilhadas entre CSV e Excel.
  const exportColumns: { header: string; value: (p: DJENPublication) => string }[] = [
    { header: 'Data Disponibilização', value: p => p.dataDisponibilizacaoFormatted || p.dataDisponibilizacao || '' },
    { header: 'Tribunal', value: p => p.siglaTribunal || '' },
    { header: 'Órgão', value: p => p.nomeOrgao || '' },
    { header: 'Tipo Comunicação', value: p => p.tipoComunicacao || '' },
    { header: 'Nº Processo', value: p => p.numeroProcessoComMascara || p.numeroProcesso || '' },
    { header: 'Classe', value: p => p.nomeClasse || '' },
    { header: 'Meio', value: p => p.meioCompleto || p.meio || '' },
    { header: 'Status', value: p => PublicationService.getStatusLabel(p.analysisStatus) },
    { header: 'Prioridade', value: p => PublicationService.getPriorityLabel(p.priority) },
    { header: 'Nº Caso Vinculado', value: p => p.caseInternalNumber || '' },
    { header: 'Link', value: p => p.link || '' },
    { header: 'Texto', value: p => (p.texto || '').slice(0, 32767) }
  ]

  const triggerDownload = (blob: Blob, filename: string) => {
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = filename
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url)
  }

  const handleExport = async (exportFormat: 'csv' | 'xlsx') => {
    try {
      setExportingPublications(true)
      toast.loading('Gerando arquivo de exportação...', { id: 'export-publications' })

      // Busca todas as publicações respeitando os filtros atuais (sem paginar a tela).
      const PAGE_SIZE = 200
      const all: DJENPublication[] = []
      let page = 1
      let totalPages = 1

      do {
        const response = await PublicationService.getPublications({
          ...filters,
          page,
          limit: PAGE_SIZE
        })
        all.push(...(response.data ?? []))
        totalPages = response.pagination?.totalPages ?? 1
        page += 1
      } while (page <= totalPages)

      if (all.length === 0) {
        toast.error('Nenhuma publicação para exportar com os filtros atuais.', {
          id: 'export-publications'
        })
        return
      }

      const baseName = `publicacoes-djen-${format(new Date(), 'yyyy-MM-dd')}`

      if (exportFormat === 'xlsx') {
        // Import dinâmico: o xlsx só entra no bundle se o usuário exportar Excel.
        const XLSX = await import('xlsx')
        const aoa = [
          exportColumns.map(c => c.header),
          ...all.map(p => exportColumns.map(c => (c.value(p) ?? '').replace(/\r?\n/g, ' ').trim()))
        ]
        const worksheet = XLSX.utils.aoa_to_sheet(aoa)
        const workbook = XLSX.utils.book_new()
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Publicações')
        const buffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' })
        triggerDownload(
          new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }),
          `${baseName}.xlsx`
        )
      } else {
        const escape = (val: string | null | undefined) => {
          const s = String(val ?? '').replace(/\r?\n/g, ' ').trim()
          return /[";,]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s
        }
        const header = exportColumns.map(c => escape(c.header)).join(';')
        const rows = all.map(p => exportColumns.map(c => escape(c.value(p))).join(';'))
        // BOM para o Excel reconhecer UTF-8 (acentos).
        const csv = '﻿' + [header, ...rows].join('\r\n')
        triggerDownload(new Blob([csv], { type: 'text/csv;charset=utf-8;' }), `${baseName}.csv`)
      }

      toast.success(`${all.length} publicaç${all.length === 1 ? 'ão exportada' : 'ões exportadas'} com sucesso!`, {
        id: 'export-publications'
      })
    } catch (error: unknown) {
      console.error('Error exporting publications:', error)
      toast.error(getErrorMessage(error, 'Erro ao exportar publicações'), {
        id: 'export-publications'
      })
    } finally {
      setExportingPublications(false)
    }
  }

  // Spinner de página só na 1ª carga: recarregar depois (ficha completa aberta) não desmonta a ficha.
  if (loading && !metrics && publications.length === 0) {
    return (
      <div className="p-4 sm:p-6">
        <LoadingSpinner />
      </div>
    )
  }

  if (error) {
    return (
      <div className="p-4 sm:p-6">
        <Card className="p-8 text-center">
          <AlertCircle className="h-12 w-12 text-destructive mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-foreground mb-2">Erro ao carregar dados</h3>
          <p className="text-muted-foreground mb-4">{error}</p>
          <Button onClick={loadData}>
            Tentar Novamente
          </Button>
        </Card>
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-4 p-4 sm:p-6">
      {/* Cabeçalho */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-col gap-1">
          <div className="flex items-center gap-2">
            <BarChart3 className="size-6 text-muted-foreground" />
            <h1 className="text-2xl font-semibold">Painel de publicações</h1>
          </div>
          <p className="text-sm text-muted-foreground">
            Métricas e lista completa das publicações do DJEN
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <Button variant="ghost" asChild>
            <Link to="/publications"><Inbox className="h-4 w-4" /> Caixa de entrada</Link>
          </Button>
          <Button
            variant="outline"
            onClick={handleToggleView}
            className="flex items-center gap-2"
            title={`Alternar para visualização ${viewMode === 'classic' ? 'semanal' : 'clássica'}`}
          >
            {viewMode === 'classic' ? (
              <>
                <BarChart3 className="h-4 w-4" />
                <span className="hidden md:inline">Visualização </span>Semanal
              </>
            ) : (
              <>
                <LayoutGrid className="h-4 w-4" />
                <span className="hidden md:inline">Visualização </span>Clássica
              </>
            )}
          </Button>

          {/* Botão para mostrar/ocultar gráficos detalhados */}
          {viewMode === 'weekly' && (
            <Button
              variant="outline"
              onClick={() => setShowDetailedCharts(!showDetailedCharts)}
              className="flex items-center gap-2"
              title={showDetailedCharts ? 'Ocultar gráficos detalhados' : 'Mostrar gráficos detalhados'}
            >
              <BarChart3 className="h-4 w-4" />
              {showDetailedCharts ? 'Ocultar Gráficos' : 'Mostrar Gráficos'}
            </Button>
          )}

          <Button
            variant="outline"
            onClick={() => setShowFilters(!showFilters)}
            className="flex items-center gap-2"
          >
            <Filter className="h-4 w-4" />
            Filtros
          </Button>

          <Button
            variant="default"
            onClick={handleManualSync}
            disabled={syncingPublications}
            className="flex items-center gap-2"
            title="Importar publicações manualmente do DJEN"
          >
            <RefreshCw className={`h-4 w-4 ${syncingPublications ? 'animate-spin' : ''}`} />
            {syncingPublications ? 'Importando...' : (
              <>
                <span className="hidden md:inline">Importar </span>Publicações
              </>
            )}
          </Button>

          <DropdownMenu>
            <DropdownMenuTrigger asChild disabled={exportingPublications}>
              <Button variant="outline">
                <Download className={`h-4 w-4 ${exportingPublications ? 'animate-pulse' : ''}`} />
                {exportingPublications ? 'Exportando...' : 'Exportar'}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => handleExport('csv')}>
                <FileText className="h-4 w-4 mr-2" />
                Exportar CSV
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleExport('xlsx')}>
                <FileSpreadsheet className="h-4 w-4 mr-2" />
                Exportar Excel (.xlsx)
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Cards de Métricas - Renderização Condicional */}
      {viewMode === 'classic' && metrics && <MetricsCards metrics={metrics} />}
      {viewMode === 'weekly' && (
        <>
          {/* Navegador de Semana (Fase 1.1) */}
          <WeekNavigator
            currentWeekStart={currentWeekStart}
            onWeekChange={handleWeekChange}
          />

          {/* Gráficos Detalhados - Colapsáveis */}
          {showDetailedCharts && (
            <div className="flex flex-col gap-4 border-t pt-4">
              {/* Calendar Heatmap (Fase 2.1) */}
              {monthlyHeatmap && (
                <CalendarHeatmap
                  data={monthlyHeatmap}
                  onDayClick={handleHeatmapDayClick}
                  onMonthChange={handleHeatmapMonthChange}
                  selectedDate={selectedHeatmapDate || undefined}
                />
              )}

              {/* Gráfico de Tendência (Fase 1.3) */}
              {weeklyTrend && (
                <WeeklyTrendChart data={weeklyTrend} />
              )}

              {/* Gráficos de Distribuição (Fase 2.2) */}
              {statusDistribution && (
                <StatusDistributionCharts
                  data={statusDistribution}
                  onSegmentClick={(type, item) => {
                    // Filtrar publicações ao clicar em segmento
                    if (type === 'status' && item.status) {
                      setFilters(prev => ({
                        ...prev,
                        analysisStatus: item.status as PublicationStatus,
                        page: 1
                      }))
                    } else if (type === 'priority' && item.priority && item.priority !== 'NONE') {
                      setFilters(prev => ({
                        ...prev,
                        priority: item.priority as PublicationPriority,
                        page: 1
                      }))
                    }
                  }}
                />
              )}

              {/* Top Tribunais (Fase 2.4) */}
              {topTribunals && topTribunals.length > 0 && (
                <TopTribunalsChart data={topTribunals} limit={5} />
              )}

              {/* Funil de Processamento (Fase 2.3) */}
              {processingFunnel && (
                <ProcessingFunnelChart data={processingFunnel} />
              )}
            </div>
          )}

          {/* Comparação Semanal (Fase 1.2) */}
          {weeklyComparison && (
            <WeekComparisonBadges comparison={weeklyComparison} />
          )}

          {/* Barra de Métricas Semanal */}
          {weeklyMetrics && (
            <CompactMetricsBar
              metrics={weeklyMetrics}
              selectedDay={selectedDay}
              onDayChange={handleDayChange}
            />
          )}
        </>
      )}

      {/* Filtros Rápidos (Fase 2) */}
      <PublicationQuickFilters
        activeFilter={activeQuickFilter}
        counts={quickFilterCounts}
        onFilterChange={handleQuickFilterChange}
        loading={loading}
      />

      {/* Barra de Pesquisa - Posicionada abaixo dos filtros rápidos */}
      <Card className="p-4">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Pesquisar publicações, processos ou advogados..."
                value={searchTerm}
                onChange={(e) => handleSearch(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>
        </div>
      </Card>

      {/* Tabela de Publicações */}
      <Card className="overflow-visible">
        <PublicationTable
          publications={publications}
          onView={handleViewPublication}
          onUpdate={handlePublicationUpdate}
        />
        
        {/* Paginação */}
        {pagination.totalPages > 1 && (
          <div className="p-4 border-t border-border">
            <Pagination
              currentPage={pagination.page}
              totalPages={pagination.totalPages}
              onPageChange={handlePageChange}
              totalItems={pagination.total}
            />
          </div>
        )}
      </Card>

      {/* Ficha completa (mesma gaveta da caixa de entrada) */}
      <PublicationFullSheet
        publicationId={selectedPublication?.id ?? null}
        onClose={handleCloseDetails}
        onChanged={handlePublicationUpdate}
      />
    </div>
  )
}